import React from 'react';
import {
  formatiDisponibili, formatoQuota, impostaFormatoQuota,
  fuso, impostaFuso, fusoDelBrowser, ascolta,
} from '../../preferenze';
import {siglaFuso} from '../../utils/formato';
import i18next from 'i18next';

/**
 * LE IMPOSTAZIONI DELLA SCHEDINA, dietro la rotellina in testata.
 *
 * Sono spunte che cambiano COME si comporta l'invio, non quanto si gioca:
 * per questo stanno in testata e non nel blocco dei soldi, dove ruberebbero
 * una riga a numeri che si leggono a ogni giocata.
 *
 * Il valore di partenza arriva SEMPRE dal profilo — dal backoffice parte
 * tutto — e qui dentro non si inventa nessun predefinito: se il profilo non
 * dice niente, la spunta nasce spenta.
 *
 * COSA FA
 *   Apre un elenco di spunte, ognuna con scritto cosa succede davvero se la
 *   lasci accesa. La spunta vale appena la tocchi: non c'e' nessun "salva",
 *   come nel resto della schedina.
 *
 * COSA NON FA
 *   Non salva niente sul profilo: la scelta del giocatore vale per la sessione
 *   e il valore del profilo resta il punto di partenza. E non mostra scelte
 *   che il servizio non sa applicare: quelle restano in vista, spente, con
 *   scritto perche'.
 */
/* I fusi proposti: quello del dispositivo, quello scelto, e un elenco corto
   per area. Non e' la lista completa (sono quattrocento): chi ne vuole un
   altro lo scrivera' nel profilo quando ci sara' il campo. */
const FUSI = [
  'Europe/Rome', 'Europe/London', 'Europe/Madrid', 'Europe/Berlin', 'Europe/Athens',
  'Europe/Istanbul', 'Europe/Moscow',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Lima',
  'America/Mexico_City', 'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Kuala_Lumpur',
  'Asia/Singapore', 'Asia/Manila', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'Africa/Lagos', 'Africa/Johannesburg', 'UTC',
];

function fusiInElenco(scelto) {
  const tutti = [fusoDelBrowser(), scelto, ...FUSI];
  return tutti.filter((z, i) => z && tutti.indexOf(z) === i);
}

export default function Impostazioni({
  aperta,
  onApri,
  /* Accetta cambio quota: la spunta che c'e' davvero. */
  cambioQuota,
  onCambioQuota,
  /* true quando il profilo estende la stessa spunta anche alla riproposta
     dell'operatore. Cambia COSA si sta accettando, quindi va scritto. */
  includeRiproposta,
}) {
  /* La tendina si chiude cliccando fuori o con Esc: senza, resta aperta
     addosso ai soldi e sul telefono non c'e' modo di toglierla. */
  const scatola = React.useRef(null);
  const [, ridisegna] = React.useState(0);
  React.useEffect(() => ascolta(() => ridisegna((n) => n + 1)), []);

  React.useEffect(() => {
    if (!aperta) return undefined;

    const fuori = (e) => {
      if (scatola.current && !scatola.current.contains(e.target)) onApri(false);
    };
    const esc = (e) => {
      if (e.key === 'Escape') onApri(false);
    };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperta, onApri]);

  return (
    <span className="lvc-sched-imposta" ref={scatola}>
      <button
        type="button"
        className={`lvc-sched-rotella${aperta ? ' aperta' : ''}`}
        aria-label={i18next.t('Impostazioni della schedina')}
        aria-expanded={aperta}
        onClick={() => onApri(!aperta)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {aperta && (
        <>
          {/* Il velo c'e' solo sul telefono, dove la tendina diventa un
              foglio dal basso: serve a dire che il resto e' sospeso. */}
          <span className="lvc-sched-velo" onClick={() => onApri(false)} />

          <div className="lvc-sched-tendina" role="group" aria-label={i18next.t('Impostazioni')}>
            <span className="lvc-sched-maniglia" />
            <p className="lvc-sched-tendina-tit">{i18next.t('Impostazioni')}</p>

            <label className="lvc-sched-voce">
              <input
                type="checkbox"
                checked={!!cambioQuota}
                onChange={(e) => onCambioQuota(e.target.checked)}
              />
              <span>
                <span className="lvc-sched-voce-nome">{i18next.t('Accetta cambio quota')}</span>
                <span className="lvc-sched-voce-dice">
                  {i18next.t('Se una quota cambia fra l’invio e la registrazione, la giocata passa al prezzo nuovo invece di essere rifiutata. Vale')}{' '}<b>{i18next.t('qualunque sia il prezzo nuovo')}</b>, e <b>{i18next.t('anche sul live')}</b>.
                  {includeRiproposta && (
                    <>{' '}{i18next.t('La stessa spunta copre anche la quota')}{' '}<b>{i18next.t('riproposta dall’operatore')}</b>.</>
                  )}
                  <br />
                  {i18next.t('Una partita già cominciata resta fuori lo stesso: quella non si gioca.')}
                </span>
              </span>
            </label>

            {/* IN VISTA MA SPENTA, col motivo scritto qui dentro.
                Si accende da sola il giorno che nasce il comportamento
                "riduci": qui non c'e' niente da cambiare. */}
            <span className="lvc-sched-voce spenta" aria-disabled="true">
              <input type="checkbox" disabled checked={false} readOnly />
              <span>
                <span className="lvc-sched-voce-nome">{i18next.t('Accetta cambio importo')}</span>
                <span className="lvc-sched-voce-dice">
                  {i18next.t('Se la posta supera un limite, giocare quello che passa invece di essere rifiutati.')}
                </span>
              </span>
            </span>
            <p className="lvc-sched-perche">
              <b>{i18next.t('Perché è spenta')}</b>
              {i18next.t('I comportamenti dei vincoli sono quattro — rifiuta, appiattisce la vincita, manda in riserva, o le ultime due insieme. Nessuno riduce la posta, quindi non c’è ancora niente da accettare.')}
            </p>

            {/* COME LEGGI LE QUOTE E LE ORE. Sono preferenze di chi guarda, non
                del sito: il server tiene decimali e UTC, qui si sceglie solo
                come scriverli. La scelta vale su tutto il sito, subito. */}
            <p className="lvc-sched-tendina-tit">{i18next.t('Come leggi')}</p>
            <label className="lvc-sched-voce lvc-sched-scelta">
              <span className="lvc-sched-voce-nome">{i18next.t('Formato quote')}</span>
              <select value={formatoQuota()}
                onChange={(e) => impostaFormatoQuota(e.target.value)}>
                {formatiDisponibili().map((f) => (
                  <option key={f.chiave} value={f.chiave} disabled={!f.concesso}>
                    {f.nome} ({f.esempio}){f.concesso ? '' : i18next.t(' — non concesso da questa skin')}
                  </option>
                ))}
              </select>
              <span className="lvc-sched-voce-dice">
                {i18next.t('Cambia solo')}{' '}<b>{i18next.t('come si scrive')}</b>{' '}{i18next.t('la quota: i conti si fanno sempre in decimale. Il predefinito lo decide la skin (')}<span className="lvc-sched-spento">{i18next.t('da collegare: campo sulla skin')}</span>).
              </span>
            </label>
            <label className="lvc-sched-voce lvc-sched-scelta">
              <span className="lvc-sched-voce-nome">{i18next.t('Fuso orario')}</span>
              <select value={fuso()} onChange={(e) => impostaFuso(e.target.value)}>
                {fusiInElenco(fuso()).map((z) => (
                  <option key={z} value={z}>{z}{z === fusoDelBrowser() ? i18next.t(' (del tuo dispositivo)') : ''}</option>
                ))}
              </select>
              <span className="lvc-sched-voce-dice">
                {i18next.t('Le ore delle partite e delle giocate si leggono in questo fuso (adesso')}{' '}<b>{siglaFuso()}</b>{i18next.t('). Il server tiene l’ora universale: nessuna ora cambia davvero, cambia solo come la vedi tu. I limiti')}{' '}<b>{i18next.t('al giorno')}</b>{' '}{i18next.t('seguono il giorno della skin, non questo.')}
              </span>
            </label>
            <p className="lvc-sched-perche">
              <b>{i18next.t('Dove si salva')}</b>
              {i18next.t('Nel browser, per ora.')}{' '}<span className="lvc-sched-spento">{i18next.t('Da collegare')}</span>{i18next.t(': il salvataggio sul conto (profilo), così la scelta ti segue su ogni dispositivo.')}
            </p>
          </div>
        </>
      )}
    </span>
  );
}
